// Слой вне Radix доживает до конца выхода (044, MOT-01, research Р2): тост, панель выбора,
// затемнение развёрнутой панели, подсказка ячейки, поиск по таблице, карточка «Входящих».
// Слой Radix так же держит Presence: правило CSS [data-state='closed'] даёт кадр выхода, а узел
// снимается по animationend. Здесь — то же для своих слоёв: state идёт в data-state, узел
// живёт, пока идёт его анимация выхода, и показывает содержимое момента закрытия (MOT-09).
import { useLayoutEffect, useState } from 'react';

/** Запас к длительности выхода: событие конца анимации в скрытой вкладке может не прийти. */
const SPARE_MS = 50;

export interface Presence<T> {
  /** Рисовать ли слой: открыт или ещё уходит. */
  mounted: boolean;
  /** В data-state: 'closed' включает кадр выхода «Табеля» или kp.css. */
  state: 'open' | 'closed';
  /** Последнее значение при open: пока слой уходит, он показывает то, что было при закрытии. */
  value: T | undefined;
  /** На узел слоя: по его анимации решается, когда слой снять. */
  ref: (node: HTMLElement | null) => void;
}

/** Анимации самого узла, которые ещё идут (кадр выхода). Без Web Animations (jsdom) — нет. */
function runningAnimations(node: HTMLElement): Animation[] {
  if (typeof node.getAnimations !== 'function') return [];
  return node.getAnimations().filter((animation) => animation.playState === 'running');
}

/** Сколько миллисекунд осталось до конца самой долгой анимации. */
function remainingMs(animations: readonly Animation[]): number {
  let longest = 0;
  for (const animation of animations) {
    const end = Number(animation.effect?.getComputedTiming().endTime ?? 0);
    const current = Number(animation.currentTime ?? 0);
    const rate = Math.abs(animation.playbackRate) || 1;
    longest = Math.max(longest, (end - current) / rate);
  }
  return Number.isFinite(longest) ? Math.max(0, longest) : 0;
}

/**
 * Слой с выходом: пока open — слой на месте; open стал false — data-state="closed", узел живёт до
 * animationend или animationcancel своей анимации (запас — таймер), потом снимается. Анимации нет
 * (кадр выхода «none», jsdom) — слой снимается сразу. Открыли снова во время выхода — тот же узел
 * возвращается ко входу, второго экземпляра нет (MOT-10).
 *
 * Результат разбирается сразу: `const { mounted, state, value, ref } = usePresence(open, count)`.
 * Линтер React (react-hooks/refs) считает весь объект со свойством ref ссылкой и не даёт читать
 * presence.state при отрисовке. value — значение простого типа или стабильное: новый объект при
 * каждой отрисовке даёт лишнюю перерисовку.
 */
export function usePresence<T = undefined>(open: boolean, value?: T): Presence<T> {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const [kept, setKept] = useState(value);

  // Смена open — во время отрисовки, без лишнего кадра: закрытие сразу рисует слой в выходе.
  if (wasOpen !== open) {
    setWasOpen(open);
    setLeaving(!open);
  }
  if (open && !Object.is(kept, value)) setKept(value);

  useLayoutEffect(() => {
    if (!leaving) return undefined;
    // Узла нет или выхода у него нет — снять до кадра, как Presence Radix: слой без кадра выхода
    // не задерживается ни на кадр.
    const animations = node ? runningAnimations(node) : [];
    if (!node || animations.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- решение по замеру узла после отрисовки
      setLeaving(false);
      return undefined;
    }
    const finish = () => setLeaving(false);
    const onEnd = (event: AnimationEvent) => {
      if (event.target !== node) return;
      if (runningAnimations(node).length === 0) finish();
    };
    node.addEventListener('animationend', onEnd);
    node.addEventListener('animationcancel', onEnd);
    const timer = window.setTimeout(finish, remainingMs(animations) + SPARE_MS);
    return () => {
      node.removeEventListener('animationend', onEnd);
      node.removeEventListener('animationcancel', onEnd);
      window.clearTimeout(timer);
    };
  }, [leaving, node]);

  return {
    mounted: open || leaving,
    state: open ? 'open' : 'closed',
    value: open ? value : kept,
    ref: setNode,
  };
}
